import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import authInterceptor from '../services/AuthInterceptor';
import { getApiUrl } from '../utils/apiConfig';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if user is authenticated on initial load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Set authorization header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Verify token validity using AuthInterceptor
      authInterceptor.authenticatedFetch(`${getApiUrl()}/api/protected-test`)
        .then(response => response.json())
        .then(data => {
          setCurrentUser(data.user);
        })
        .catch(() => {
          // If token is invalid, remove it
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
      setError('');
      const response = await axios.post(
        `${getApiUrl()}/api/auth/login`, 
        { username, password }
      );
      
      const { token, user } = response.data;
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Set authorization header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setCurrentUser(user);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to login');
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout API if you're tracking sessions server-side
      await axios.post(`${getApiUrl()}/api/auth/logout`);
    } catch (err) {
      console.error('Logout API error:', err);
    } finally {
      // Remove token from localStorage and reset auth state regardless of API success
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setCurrentUser(null);
    }
  };

  const value = {
    currentUser,
    login,
    logout,
    isAuthenticated: !!currentUser,
    error,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 